"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

export interface NumberTickerProps {
  /** 目标数值，未提供时显示 placeholder */
  value?: number;
  /** 动画方向：up 从 0 增至 value，down 从 value 减至 0 */
  direction?: "up" | "down";
  /** 动画时长（毫秒） */
  duration?: number;
  /** 延迟开始（毫秒） */
  delay?: number;
  /** 小数位数 */
  decimalPlaces?: number;
  /** 未提供 value 时显示的内容 */
  placeholder?: string;
  className?: string;
}

/**
 * 数字滚动组件：从起始值过渡到目标值的动画展示。
 * 支持进入视口后再开始动画（可选），与 Vue 版 NumberTicker 行为一致。
 */
function setRef<T>(ref: React.Ref<T> | undefined, el: T | null) {
  if (typeof ref === "function") ref(el);
  else if (ref && typeof ref === "object") (ref as React.MutableRefObject<T | null>).current = el;
}

const NumberTicker = React.forwardRef<HTMLSpanElement, NumberTickerProps>(
  (
    {
      value = 0,
      direction = "up",
      duration = 1000,
      delay = 0,
      decimalPlaces = 0,
      placeholder = "–",
      className,
    },
    ref
  ) => {
    const internalRef = React.useRef<HTMLSpanElement>(null);
    const elementRef = internalRef;

    const [displayValue, setDisplayValueState] = React.useState(
      direction === "down" ? value : 0
    );
    const displayValueRef = React.useRef(displayValue);
    const setDisplayValue = React.useCallback((v: number) => {
      displayValueRef.current = v;
      setDisplayValueState(v);
    }, []);

    const [hasBeenInView, setHasBeenInView] = React.useState(false);
    const hasAnimatedRef = React.useRef(false);

    // 同步 ref
    React.useEffect(() => {
      displayValueRef.current = displayValue;
    }, [displayValue]);

    // 进入视口检测
    React.useEffect(() => {
      const el = elementRef.current;
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry?.isIntersecting) {
            setHasBeenInView(true);
          }
        },
        { threshold: 0 }
      );
      obs.observe(el);
      return () => obs.disconnect();
    }, [elementRef]);

    // 未进入视口时保持初始显示
    React.useEffect(() => {
      if (!hasBeenInView) {
        setDisplayValue(direction === "down" ? value : 0);
      }
    }, [hasBeenInView, direction, value]);

    // 数值过渡动画
    React.useEffect(() => {
      if (!hasBeenInView || value == null) return;

      const target = direction === "up" ? value : 0;
      const startFrom = hasAnimatedRef.current
        ? displayValueRef.current
        : direction === "up"
          ? 0
          : value;
      hasAnimatedRef.current = true;

      let startTime: number | null = null;
      let rafId: number;

      const tick = (timestamp: number) => {
        if (startTime === null) startTime = timestamp;
        const elapsed = timestamp - startTime;
        if (elapsed < delay) {
          rafId = requestAnimationFrame(tick);
          return;
        }
        const t = Math.min((elapsed - delay) / duration, 1);
        const eased = easeOutCubic(t);
        const current = startFrom + (target - startFrom) * eased;
        setDisplayValue(Number(current.toFixed(decimalPlaces)));
        if (t < 1) {
          rafId = requestAnimationFrame(tick);
        }
      };

      rafId = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(rafId);
    }, [hasBeenInView, value, direction, duration, delay, decimalPlaces]);

    const setSpanRef = React.useCallback(
      (el: HTMLSpanElement | null) => {
        (internalRef as React.MutableRefObject<HTMLSpanElement | null>).current = el;
        setRef(ref, el);
      },
      [ref]
    );

    if (value === undefined || value === null) {
      return (
        <span
          ref={setSpanRef}
          className={cn("inline-block tabular-nums tracking-wider", className)}
        >
          {placeholder}
        </span>
      );
    }

    const formatted = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(displayValue);

    return (
      <span
        ref={setSpanRef}
        className={cn("inline-block tabular-nums tracking-wider", className)}
      >
        {formatted}
      </span>
    );
  }
);

NumberTicker.displayName = "NumberTicker";

export { NumberTicker };
